export type DisciplinaryStatus = 'draft' | 'issued' | 'acknowledged'

export type ViolationType =
  | 'attendance_tardiness'
  | 'work_performance'
  | 'policy_violation'
  | 'safety_violation'
  | 'insubordination'
  | 'harassment_misconduct'
  | 'substance_abuse'
  | 'property_damage'
  | 'time_fraud'
  | 'other'

export type DisciplineAction =
  | 'verbal_warning'
  | 'written_warning_1st'
  | 'written_warning_2nd'
  | 'written_warning_final'
  | 'performance_improvement_plan'
  | 'suspension_unpaid'
  | 'suspension_paid'
  | 'termination'
  | 'other'

export const VIOLATION_TYPE_LABELS: Record<ViolationType, string> = {
  attendance_tardiness: 'Attendance / Tardiness',
  work_performance: 'Work Performance',
  policy_violation: 'Policy Violation',
  safety_violation: 'Safety Violation',
  insubordination: 'Insubordination',
  harassment_misconduct: 'Harassment / Misconduct',
  substance_abuse: 'Substance Abuse',
  property_damage: 'Property Damage',
  time_fraud: 'Time / Payroll Fraud',
  other: 'Other',
}

export const DISCIPLINE_ACTION_LABELS: Record<DisciplineAction, string> = {
  verbal_warning: 'Verbal Warning (Documented)',
  written_warning_1st: '1st Written Warning',
  written_warning_2nd: '2nd Written Warning',
  written_warning_final: 'Final Written Warning',
  performance_improvement_plan: 'Performance Improvement Plan (PIP)',
  suspension_unpaid: 'Suspension Without Pay',
  suspension_paid: 'Suspension With Pay (Pending Investigation)',
  termination: 'Termination',
  other: 'Other',
}

export const DISCIPLINE_ACTIONS_ORDERED: DisciplineAction[] = [
  'verbal_warning',
  'written_warning_1st',
  'written_warning_2nd',
  'written_warning_final',
  'performance_improvement_plan',
  'suspension_unpaid',
  'suspension_paid',
  'termination',
  'other',
]

export const VIOLATION_TYPES_ORDERED: ViolationType[] = [
  'attendance_tardiness',
  'work_performance',
  'policy_violation',
  'safety_violation',
  'insubordination',
  'harassment_misconduct',
  'substance_abuse',
  'property_damage',
  'time_fraud',
  'other',
]

export interface DisciplinaryReport {
  id: string
  report_number: string
  status: DisciplinaryStatus
  date_of_report: string | null
  // Section 1
  employee_name: string | null
  employee_id: string | null
  department: string | null
  position_title: string | null
  supervisor_name: string | null
  date_of_hire: string | null
  date_of_incident: string | null
  time_of_incident: string | null
  location: string | null
  // Section 2
  violation_types: ViolationType[]
  prior_discipline: boolean
  prior_discipline_details: string | null
  // Section 3
  discipline_action: DisciplineAction | null
  suspension_days: number | null
  pip_duration_days: number | null
  // Section 4
  incident_description: string | null
  policy_violated: string | null
  witnesses: string | null
  // Section 5
  corrective_action_required: string | null
  employee_statement: string | null
  // Section 6
  employee_comments: string | null
  // Section 7
  issuer_name: string | null
  issuer_title: string | null
  issuer_signed_at: string | null
  employee_ack_name: string | null
  employee_ack_at: string | null
  employee_refused_to_sign: boolean
  refusal_reason: string | null
  // Meta
  created_by: string | null
  pdf_file_id: string | null
  created_at: string | null
  updated_at: string | null
}

export interface DisciplinaryListResponse {
  items: DisciplinaryReport[]
  total: number
  page: number
  limit: number
}

export interface DisciplinaryStats {
  total: number
  by_status: Record<string, number>
  draft: number
  issued: number
  acknowledged: number
}

export interface GeneratePdfResponse {
  file_id: string
  filename: string
}

export type DisciplinaryWrite = Omit<
  DisciplinaryReport,
  | 'id'
  | 'report_number'
  | 'status'
  | 'created_by'
  | 'pdf_file_id'
  | 'created_at'
  | 'updated_at'
  | 'issuer_signed_at'
  | 'employee_ack_at'
>
